import type { Tracker } from '@archipro-website/tracker';
import type { CreateEnquirySource } from '~/modules/enquiry';
import type { EnquiryItemType } from './trackEnquireReveal';

/**
 * Where a Book a meeting button sits: the enquiry source the same button
 * reports, plus the openings only the gate has.
 */
export type BookMeetingRevealSource =
    | CreateEnquirySource
    /** Opened by a `?book=1` link rather than a click. */
    | 'BookMeetingAutoOpen'
    /** A button that names no surface. */
    | 'BookMeetingModal';

export interface TrackBookMeetingRevealProps {
    professionalName: string;
    professionalID: number;
    itemID: number;
    itemType: EnquiryItemType;
    source: BookMeetingRevealSource;
    ctaLabel?: string;
    projectID?: number;
    /** The team member the button books, for a person's own Book button. */
    teamMemberID?: number;
}

/**
 * Track the `BookMeetingReveal` event: the Book a meeting gate opening for the
 * visitor. Shaped as `EnquiryReveal` is, so the two compare side by side. It
 * is the visitor asking to see times, not a lead.
 *
 * Only `Source` and `ExtraData` reach the warehouse, so the item, practice and
 * team member also travel in `ExtraData` as JSON.
 */
export const trackBookMeetingReveal = (
    tracker: Tracker,
    props: TrackBookMeetingRevealProps,
) => {
    tracker.log('BookMeetingReveal', {
        url: new URL(window.location.href),
        label: props.professionalName,
        data: {
            Source: props.source,
            content_ids: [`${props.itemType}_${props.itemID}`],
            content_type: props.itemType,
            CTA_Label: props.ctaLabel || 'Book a meeting',
            projectID: props.projectID || '',
            professionalID: props.professionalID,
            ExtraData: JSON.stringify({
                itemType: props.itemType,
                itemID: props.itemID,
                professionalID: props.professionalID,
                ...(props.teamMemberID !== undefined && {
                    teamMemberID: props.teamMemberID,
                }),
            }),
        },
        targetTracker: ['archiproTracker', 'ga4Tracker'],
    });
};
